import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Cookies } from 'react-cookie';
import {
    createBrowserRouter,
    RouterProvider,
} from 'react-router-dom';
import {
    AlertContext,
    type AlertContextProps,
    type AlertParams,
    type Language,
    LanguageContext,
    type LanguageContextProps,
    type LanguageNamespaceStatus,
} from '@ifrc-go/ui/contexts';
import {
    isDefined,
    unique,
} from '@togglecorp/fujs';
import { cacheExchange } from '@urql/exchange-graphcache';
import {
    Client as UrqlClient,
    fetchExchange,
    Provider as UrqlProvider,
} from 'urql';

import HelpChoosingContext, {
    type FormValues,
    type HelpChoosingContextProps,
} from '#contexts/HelpChoosing';
import RouteContext from '#contexts/route';
import schema from '#generated/schema.json';
import { KEY_LANGUAGE_STORAGE } from '#utils/constants';
import {
    getFromStorage,
    setToStorage,
} from '#utils/localStorage';
import { resolveUrl } from '#utils/resolveUrl';

import wrappedRoutes, { unwrappedRoutes } from './routes';

const router = createBrowserRouter(unwrappedRoutes);

const baseString: LanguageContextProps['strings'] = {};

const COOKIE_NAME = `RCSELECT-${import.meta.env.APP_ENVIRONMENT}-CSRFTOKEN`;
const GRAPHQL_ENDPOINT = resolveUrl(import.meta.env.APP_API_DOMAIN, 'graphql/');

const cookies = new Cookies();

const gqlClient = new UrqlClient({
    url: GRAPHQL_ENDPOINT,
    exchanges: [
        cacheExchange({
            keys: new Proxy(
                {
                    AppEnumCollection: () => null,
                },
                {
                    get(target, prop) {
                        if (typeof prop === 'string' && prop.endsWith('Enum')) {
                            return (data: { key: string }) => data.key;
                        }

                        const fallback = (data: { id: string }) => data.id;

                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        return (target as any)[prop] || fallback;
                    },
                },
            ),
            schema,
        }),
        fetchExchange,
    ],
    fetchOptions: () => ({
        headers: {
            'X-CSRFToken': cookies.get(COOKIE_NAME),
        },
        credentials: 'include',
    }),
    requestPolicy: 'cache-and-network',
});

function App() {
    const [
        strings,
        setStrings,
    ] = useState<Record<string, LanguageContextProps['strings']>>({});

    const [currentLanguage, setCurrentLanguage] = useState<Language>('en');

    // FIXME: this is not used
    const [
        languageNamespaceStatus,
        setLanguageNamespaceStatus,
    ] = useState<Record<string, LanguageNamespaceStatus>>({});

    useEffect(() => {
        const language = getFromStorage<Language>(KEY_LANGUAGE_STORAGE);
        setCurrentLanguage(language ?? 'en');
    }, []);

    const setAndStoreCurrentLanguage = useCallback(
        (newLanguage: Language) => {
            setCurrentLanguage(newLanguage);
            setToStorage(KEY_LANGUAGE_STORAGE, newLanguage);
        },
        [],
    );

    // Hydration
    useEffect(() => {
        const language = getFromStorage<Language>(KEY_LANGUAGE_STORAGE);
        setCurrentLanguage(language ?? 'en');
    }, []);

    const registerLanguageNamespace = useCallback(
        (namespace: string, fallbackStrings: Record<string, string>) => {
            setStrings(
                (prevValue) => {
                    if (isDefined(prevValue[currentLanguage]?.[namespace])) {
                        return {
                            ...prevValue,
                            [currentLanguage]: {
                                ...prevValue[currentLanguage],
                                [namespace]: {
                                    ...fallbackStrings,
                                    ...prevValue[currentLanguage]?.[namespace],
                                },
                            },
                        };
                    }

                    return {
                        ...prevValue,
                        [currentLanguage]: {
                            ...prevValue[currentLanguage],
                            [namespace]: fallbackStrings,
                        },
                    };
                },
            );
        },
        [setStrings, currentLanguage],
    );

    const setStringsForCurrentLang = useCallback<Dispatch<SetStateAction<LanguageContextProps['strings']>>>(
        (value) => {
            setStrings((oldValue) => ({
                ...oldValue,
                [currentLanguage]: typeof value === 'function'
                    ? value(oldValue[currentLanguage])
                    : value,
            }));
        },
        [currentLanguage],
    );

    const languageContextValue = useMemo<LanguageContextProps>(
        () => ({
            languageNamespaceStatus,
            setLanguageNamespaceStatus,
            currentLanguage,
            setCurrentLanguage: setAndStoreCurrentLanguage,
            strings: strings[currentLanguage] ?? baseString,
            setStrings: setStringsForCurrentLang,
            registerNamespace: registerLanguageNamespace,
        }),
        [
            languageNamespaceStatus,
            setLanguageNamespaceStatus,
            currentLanguage,
            setAndStoreCurrentLanguage,
            strings,
            registerLanguageNamespace,
            setStringsForCurrentLang,
        ],
    );

    const [alerts, setAlerts] = useState<AlertParams[]>([]);

    const [formValues, setFormValues] = useState<FormValues>();

    // Hydration
    useEffect(() => {
        const language = getFromStorage<Language>(KEY_LANGUAGE_STORAGE);
        setCurrentLanguage(language ?? 'en');
    }, []);

    const helpChoosingContext = useMemo<HelpChoosingContextProps>(
        () => ({
            formValues,
            setFormValues,
        }),
        [formValues],
    );
    const addAlert = useCallback((alert: AlertParams) => {
        setAlerts((prevAlerts) => unique(
            [...prevAlerts, alert],
            (a) => a.name,
        ) ?? prevAlerts);
    }, [setAlerts]);

    const removeAlert = useCallback((name: AlertParams['name']) => {
        setAlerts((prevAlerts) => {
            const i = prevAlerts.findIndex((a) => a.name === name);
            if (i === -1) {
                return prevAlerts;
            }

            const newAlerts = [...prevAlerts];
            newAlerts.splice(i, 1);

            return newAlerts;
        });
    }, [setAlerts]);

    const updateAlert = useCallback((name: AlertParams['name'], paramsWithoutName: Omit<AlertParams, 'name'>) => {
        setAlerts((prevAlerts) => {
            const i = prevAlerts.findIndex((a) => a.name === name);
            if (i === -1) {
                return prevAlerts;
            }

            const updatedAlert = {
                ...prevAlerts[i],
                paramsWithoutName,
            };

            const newAlerts = [...prevAlerts];
            newAlerts.splice(i, 1, updatedAlert);

            return newAlerts;
        });
    }, [setAlerts]);

    const alertContextValue: AlertContextProps = useMemo(() => ({
        alerts,
        addAlert,
        updateAlert,
        removeAlert,
    }), [alerts, addAlert, updateAlert, removeAlert]);

    return (
        <UrqlProvider value={gqlClient}>
            <RouteContext.Provider value={wrappedRoutes}>
                <HelpChoosingContext.Provider value={helpChoosingContext}>
                    <AlertContext.Provider value={alertContextValue}>
                        <LanguageContext.Provider value={languageContextValue}>
                            <RouterProvider router={router} />
                        </LanguageContext.Provider>
                    </AlertContext.Provider>
                </HelpChoosingContext.Provider>
            </RouteContext.Provider>
        </UrqlProvider>
    );
}

export default App;
